export type DiscountType = 'PERCENT' | 'AMOUNT';

export interface Coupon {
  id: number;
  code: string;
  locationId: number;
  locationName?: string;
  discountType: DiscountType;
  discountValue: number;
  minOrderAmount: number;
  usageLimit: number;
  usedCount: number;
  isActive: boolean;
  combinableWithDiscounts: boolean;
  // Wykluczaja sie wzajemnie (backend: error.coupon_customer_rules_conflict).
  firstOrderOnly: boolean;
  perCustomerLimit: number | null;
}

// Gorna granica jak w CouponRequest na BE (@Max(1000)).
export const MAX_PER_CUSTOMER_LIMIT = 1000;

// Kod wpisywany przez klienta: wielkie litery, cyfry, "-" i "_".
export const COUPON_CODE_PATTERN = /^[A-Z0-9_-]{3,32}$/;

// Rabat dotyczy tylko jedzenia (bez dostawy) i po jego odjeciu z jedzenia musi zostac
// co najmniej MIN_FOOD_AFTER_DISCOUNT. Ta sama regula jest walidowana na BE
// (error.coupon_min_order_amount_too_low).
export const MIN_FOOD_AFTER_DISCOUNT = 5;

// Minimalna dopuszczalna kwota zamowienia dla danego rabatu, zaokraglona w gore do grosza.
// Liczone w groszach, zeby uniknac bledow zmiennoprzecinkowych (np. 10.1 + 5).
export const minRequiredOrderAmount = (discountType: DiscountType, discountValue: number): number | null => {
  if (!Number.isFinite(discountValue) || discountValue <= 0) return null;
  if (discountType === 'AMOUNT') {
    return Math.round(discountValue * 100 + MIN_FOOD_AFTER_DISCOUNT * 100) / 100;
  }
  if (discountValue >= 100) return null;
  const grosze = (MIN_FOOD_AFTER_DISCOUNT * 100 * 100) / (100 - discountValue);
  return Math.ceil(grosze - 1e-9) / 100;
};

/** Symbol waluty do pola kwoty: PLN -> "zł", EUR -> "€", USD -> "$". */
export const currencySymbol = (currency?: string | null): string => {
  const code = currency || 'PLN';
  try {
    return (
      new Intl.NumberFormat('pl-PL', { style: 'currency', currency: code, currencyDisplay: 'narrowSymbol' })
        .formatToParts(0)
        .find((part) => part.type === 'currency')?.value ?? code
    );
  } catch {
    return code;
  }
};

export const formatMoney = (value: number, currency = 'PLN') =>
  new Intl.NumberFormat('pl-PL', { style: 'currency', currency }).format(value);

export const formatDiscount = (coupon: Pick<Coupon, 'discountType' | 'discountValue'>, currency = 'PLN') =>
  coupon.discountType === 'PERCENT'
    ? `−${coupon.discountValue}%`
    : `−${formatMoney(coupon.discountValue, currency)}`;
